import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setToken(params.get("token") || "");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (!token) {
      toast.error("Reset token is missing.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pwd });
      setDone(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthLayout title="Password updated" subtitle="You can now log in with your new password.">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center" data-testid="reset-success">
          <CheckCircle2 className="h-10 w-10 text-blue-600 mx-auto" />
          <p className="text-sm text-slate-700 mt-3">Your password has been reset successfully.</p>
          <Button onClick={() => navigate("/login")} className="mt-5 bg-blue-600 hover:bg-blue-700 text-white" data-testid="reset-login-btn">
            Go to login
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Enter and confirm your new password below."
      footer={
        <Link to="/login" className="text-blue-700 font-semibold hover:underline" data-testid="reset-back-login">
          Back to login
        </Link>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        {!params.get("token") && (
          <div>
            <Label htmlFor="rp-token">Reset token</Label>
            <Input
              id="rp-token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              placeholder="Paste your token here"
              className="mt-1.5 h-11"
              data-testid="reset-input-token"
            />
          </div>
        )}
        <div>
          <Label htmlFor="rp-pwd">New password</Label>
          <div className="relative mt-1.5">
            <Input
              id="rp-pwd"
              type={show ? "text" : "password"}
              required
              minLength={6}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="h-11 pr-10"
              data-testid="reset-input-password"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
              aria-label="Toggle password"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label htmlFor="rp-confirm">Confirm password</Label>
          <Input
            id="rp-confirm"
            type={show ? "text" : "password"}
            required
            minLength={6}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="mt-1.5 h-11"
            data-testid="reset-input-confirm"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="reset-submit-btn"
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold"
        >
          <Lock className="h-4 w-4 mr-2" />
          {loading ? "Updating…" : "Update password"}
        </Button>
      </form>
    </AuthLayout>
  );
}
